import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  ChildCare as ChildCareIcon,
  Link as LinkIcon,
  LinkOff as UnlinkIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { 
  searchChildPatients, 
  linkChildPatient, 
  unlinkChildPatient, 
  getLinkedChildren 
} from '../../services/patientService';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiCardHeader-root': {
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const LinkRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== 'isChild'
})(({ theme, isChild }) => ({
  backgroundColor: isChild ? theme.palette.grey[50] : theme.palette.background.paper,
  '&:hover': {
    backgroundColor: isChild ? theme.palette.grey[100] : theme.palette.action.hover,
  },
}));

const ChildCell = styled(TableCell)(({ theme }) => ({
  paddingLeft: theme.spacing(4), // Indent child rows
  borderLeft: `3px solid ${theme.palette.primary.main}`,
}));

const PatientLinkTab = ({ patient, editMode = false }) => {
  const [linkedChildren, setLinkedChildren] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Add link dialog state
  const [addLinkDialogOpen, setAddLinkDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedChildPatient, setSelectedChildPatient] = useState(null);
  const [linking, setLinking] = useState(false);

  const loadLinkedChildren = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getLinkedChildren(patient.id);
      console.log('Linked children response:', response);
      console.log('Linked children data:', response.linkedChildren);
      
      if (response.linkedChildren && response.linkedChildren.length > 0) {
        response.linkedChildren.forEach((child, index) => {
          console.log(`Child ${index} linkedAt:`, child.linkedAt, 'type:', typeof child.linkedAt);
        });
      }
      
      setLinkedChildren(response.linkedChildren || []);
    } catch (err) {
      console.error('Error loading linked children:', err);
      setError('Failed to load linked children');
    } finally {
      setLoading(false);
    }
  }, [patient?.id]);
  
  // Load linked children when component mounts or patient changes
  useEffect(() => {
    if (patient?.id) {
      loadLinkedChildren();
    }
  }, [patient?.id, loadLinkedChildren]);



  const handleSearchChildren = useCallback(async (searchValue) => {
    if (!searchValue || searchValue.length < 2) {
      setSearchResults([]);
      setSelectedChildPatient(null);
      return;
    }

    try {
      setSearchLoading(true);
      const response = await searchChildPatients(searchValue, 20, patient?.id);
      const results = response.results || [];
      setSearchResults(results);
      
      // Clear selected patient if it's no longer in results or if it's already linked
      if (selectedChildPatient) {
        const currentResult = results.find(r => r.id === selectedChildPatient.id);
        if (!currentResult || currentResult.linkedToAudit) {
          setSelectedChildPatient(null);
        }
      }
    } catch (err) {
      console.error('Error searching children:', err);
      setSnackbar({
        open: true,
        message: 'Error searching for child patients',
        severity: 'error'
      });
    } finally {
      setSearchLoading(false);
    }
  }, [patient?.id, selectedChildPatient]);

  const handleAddLink = async () => {
    if (!selectedChildPatient) {
      setSnackbar({
        open: true,
        message: 'Please select a child patient',
        severity: 'warning'
      });
      return;
    }

    try {
      setLinking(true);
      await linkChildPatient(patient.id, selectedChildPatient.id);
      
      setSnackbar({
        open: true,
        message: 'Child patient linked successfully',
        severity: 'success'
      });
      
      // Refresh the list and close dialog
      await loadLinkedChildren();
      setAddLinkDialogOpen(false);
      setSelectedChildPatient(null);
      setSearchTerm('');
      setSearchResults([]);
    } catch (err) {
      console.error('Error linking child patient:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Error linking child patient',
        severity: 'error'
      });
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkChild = async (childPatientId) => {
    try {
      await unlinkChildPatient(patient.id, childPatientId);
      
      setSnackbar({
        open: true,
        message: 'Child patient unlinked successfully',
        severity: 'success'
      });
      
      // Refresh the list
      await loadLinkedChildren();
    } catch (err) {
      console.error('Error unlinking child patient:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Error unlinking child patient',
        severity: 'error'
      });
    }
  };

  const formatDate = (dateInput) => {
    if (!dateInput) return 'Unknown';
    
    
    try {
      let date;
      
      // Handle Firestore timestamp objects
      if (dateInput && typeof dateInput === 'object' && dateInput.toDate) {
        console.log('Using toDate() method');
        date = dateInput.toDate();
      } else if (dateInput && typeof dateInput === 'object' && dateInput.seconds) {
        // Handle Firestore timestamp with seconds
        console.log('Using seconds property:', dateInput.seconds);
        date = new Date(dateInput.seconds * 1000);
      } else if (dateInput && typeof dateInput === 'object' && dateInput._seconds) {
        // Handle Firestore timestamp with _seconds (private property)
        console.log('Using _seconds property:', dateInput._seconds);
        date = new Date(dateInput._seconds * 1000);
      } else if (dateInput && typeof dateInput === 'object' && dateInput.nanoseconds) {
        // Handle Firestore timestamp with nanoseconds
        console.log('Using nanoseconds property:', dateInput.nanoseconds);
        date = new Date(dateInput.nanoseconds / 1000000);
      } else {
        // Handle regular date strings or Date objects
        console.log('Using new Date() constructor');
        date = new Date(dateInput);
      }
      
      console.log('Final date object:', date);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.log('Date is invalid');
        return 'Invalid date';
      }
      
      const formatted = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
      console.log('Formatted result:', formatted);
      return formatted;
    } catch (error) {
      console.error('Error formatting date:', error, dateInput);
      return 'Invalid date';
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 'Unknown';
    try {
      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    } catch {
      return 'Unknown';
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (!patient) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No patient selected
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with Add Link button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LinkIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="h3">
            Patient Links
          </Typography>
        </Box>
        {editMode && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddLinkDialogOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            Add Child Link
          </Button>
        )}
      </Box>

      {/* Current patient info */}
      <StyledCard sx={{ mb: 3 }}>
        <CardHeader
          title="Current Patient"
          avatar={<PersonIcon color="primary" />}
        />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Name:</strong> {patient.firstName} {patient.lastName}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Age:</strong> {calculateAge(patient.dateOfBirth)} years
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Date of Birth:</strong> {formatDate(patient.dateOfBirth)}
              </Typography>
            </Grid>
                         <Grid item xs={12} md={6}>
               <Typography variant="body2" color="text.secondary">
                 <strong>Status:</strong> {calculateAge(patient.dateOfBirth) >= 18 ? 'Adult' : 'Minor'}
               </Typography>
             </Grid>
          </Grid>
        </CardContent>
      </StyledCard>

      {/* Linked children table */}
      <StyledCard>
        <CardHeader
          title="Linked Child Patients"
          avatar={<ChildCareIcon color="primary" />}
          action={
            <Chip 
              label={`${linkedChildren.length} linked`} 
              color="primary" 
              variant="outlined" 
              size="small" 
            />
          }
        />
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : linkedChildren.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ChildCareIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                No child patients linked
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {editMode ? 'Click "Add Child Link" to link a child patient' : 'No child patients are currently linked to this adult'}
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Age</TableCell>
                    <TableCell>Date of Birth</TableCell>
                    <TableCell>Facility</TableCell>
                    <TableCell>Linked Date</TableCell>
                    {editMode && <TableCell align="center">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {linkedChildren.map((child) => (
                    <LinkRow key={child.id} isChild={true}>
                      <ChildCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ChildCareIcon sx={{ mr: 1, fontSize: 20, color: 'primary.main' }} />
                          {child.name}
                        </Box>
                      </ChildCell>
                      <ChildCell>{calculateAge(child.dateOfBirth)} years</ChildCell>
                      <ChildCell>{formatDate(child.dateOfBirth)}</ChildCell>
                      <ChildCell>{child.facilityName || 'Unknown'}</ChildCell>
                      <ChildCell>{formatDate(child.linkedAt)}</ChildCell>
                      {editMode && (
                        <ChildCell align="center">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleUnlinkChild(child.id)}
                            title="Unlink child patient"
                          >
                            <UnlinkIcon />
                          </IconButton>
                        </ChildCell>
                      )}
                    </LinkRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </StyledCard>

      {/* Add Link Dialog */}
      <Dialog 
        open={addLinkDialogOpen} 
        onClose={() => setAddLinkDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LinkIcon sx={{ mr: 1, color: 'primary.main' }} />
            Link Child Patient
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Search for patients under 18 years old to link to this adult patient. 
              The child patient will be updated with a reference to this adult.
            </Typography>
            
            <TextField
              fullWidth
              label="Search for child patients"
              placeholder="Enter patient name (minimum 2 characters)"
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value;
                setSearchTerm(value);
                if (value.length >= 2) {
                  handleSearchChildren(value);
                } else {
                  setSearchResults([]);
                  setSelectedChildPatient(null);
                }
              }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                endAdornment: searchLoading && <CircularProgress size={20} />
              }}
              sx={{ mb: 2 }}
            />

            {searchResults.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Search Results ({searchResults.length})
                  {(() => {
                    const linkedCount = searchResults.filter(r => r.linkedToAudit).length;
                    return linkedCount > 0 ? (
                      <Chip 
                        label={`${linkedCount} already linked`} 
                        size="small" 
                        color="warning" 
                        variant="outlined"
                        sx={{ ml: 1 }}
                      />
                    ) : null;
                  })()}
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Age</TableCell>
                        <TableCell>Date of Birth</TableCell>
                        <TableCell>Facility</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {searchResults.map((result) => {
                        const isAlreadyLinked = result.linkedToAudit;
                        const isSelected = selectedChildPatient?.id === result.id;
                        
                        return (
                          <TableRow 
                            key={result.id}
                            hover={!isAlreadyLinked}
                            selected={isSelected && !isAlreadyLinked}
                            onClick={() => !isAlreadyLinked && setSelectedChildPatient(result)}
                            sx={{ 
                              cursor: isAlreadyLinked ? 'not-allowed' : 'pointer',
                              opacity: isAlreadyLinked ? 0.6 : 1,
                              backgroundColor: isAlreadyLinked ? 'rgba(0, 0, 0, 0.04)' : 'inherit'
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <ChildCareIcon sx={{ mr: 1, fontSize: 18, color: 'primary.main' }} />
                                {result.name}
                                {isAlreadyLinked && (
                                  <Chip 
                                    label="Already Linked" 
                                    size="small" 
                                    color="warning" 
                                    variant="outlined"
                                    sx={{ ml: 1 }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>{result.age} years</TableCell>
                            <TableCell>{formatDate(result.dateOfBirth)}</TableCell>
                            <TableCell>{result.facilityName || 'Unknown'}</TableCell>
                            <TableCell>
                                                          <Tooltip 
                              title={isAlreadyLinked ? "This patient is already linked to another adult" : ""}
                              disableHoverListener={!isAlreadyLinked}
                            >
                              <span>
                                <Button
                                  size="small"
                                  variant={isSelected ? "contained" : "outlined"}
                                  disabled={isAlreadyLinked}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!isAlreadyLinked) {
                                      setSelectedChildPatient(result);
                                    }
                                  }}
                                >
                                  {isAlreadyLinked ? 'Already Linked' : (isSelected ? 'Selected' : 'Select')}
                                </Button>
                              </span>
                            </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {selectedChildPatient && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Selected:</strong> {selectedChildPatient.name} ({selectedChildPatient.age} years old)
                  <br />
                  <strong>Facility:</strong> {selectedChildPatient.facilityName || 'Unknown'}
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddLinkDialogOpen(false);
            setSelectedChildPatient(null);
            setSearchTerm('');
            setSearchResults([]);
          }} disabled={linking}>
            Cancel
          </Button>
          <Button
            onClick={handleAddLink}
            variant="contained"
            disabled={!selectedChildPatient || linking}
            startIcon={linking ? <CircularProgress size={16} /> : <LinkIcon />}
          >
            {linking ? 'Linking...' : 'Link Patient'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PatientLinkTab;
